import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../services/api';
import { MainLayout } from '../components/shared/MainLayout';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { ThisWeeksBestLesson } from '../components/learn/ThisWeeksBestLesson';
import { ProgressCard } from '../components/learn/ProgressCard';
import {
  GraduationCap, ArrowLeft, Spinner, Heart, Pill, Stethoscope,
  CalendarBlank, Heartbeat, CaretRight, CaretDown, ArrowsClockwise,
  ChatCircle, BookOpen, ShieldCheck, Lightning, MagnifyingGlass,
  ThumbsUp, ThumbsDown, CheckCircle, Target, PlusCircle, Check,
  Notebook, Trash, X, Eye, Warning, ArrowRight, Trophy
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPE_CONFIG = {
  condition: { icon: Heart, color: 'text-rose-500', bg: 'bg-rose-50', border: 'border-rose-200', label: 'Condition', path: '/health' },
  medication: { icon: Pill, color: 'text-blue-500', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Medication', path: '/health' },
  symptom: { icon: Heartbeat, color: 'text-amber-500', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Symptom', path: '/health' },
  appointment: { icon: CalendarBlank, color: 'text-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Appointment', path: '/appointments' },
  specialist: { icon: Stethoscope, color: 'text-violet-500', bg: 'bg-violet-50', border: 'border-violet-200', label: 'Specialist', path: '/medical-team' },
  doctor: { icon: Stethoscope, color: 'text-violet-500', bg: 'bg-violet-50', border: 'border-violet-200', label: 'Doctor', path: '/medical-team' },
};

const SECTION_ICONS = {
  'Why this matters right now': Lightning,
  'What to do right now': Target,
  'What this is': BookOpen,
  'What it means for you': Heart,
  'What to watch for': ShieldCheck,
  'What typically happens': ArrowsClockwise,
  'How this is usually managed': Stethoscope,
  'Questions for your doctor': ChatCircle,
  // Course-specific sections
  'Why this matters for you': Heart,
  'What to do day-to-day': Target,
  'What can go wrong': Warning,
  'How this connects to your other conditions': Lightning,
};

// Stage-13 — split chips by whether the user has any health data on file.
// "Personalised" examples assume conditions/meds; "neutral" examples are
// universally relatable for a brand-new user.
const COURSE_EXAMPLES_PERSONAL = [
  'How does lithium affect my kidneys?',
  'Why do I get chest pain with my condition?',
  'How do my medications interact?',
  'How do I manage my sleep with bipolar?',
];
const COURSE_EXAMPLES_NEUTRAL = [
  'How do I read a blood test report?',
  'What does a normal range actually mean?',
  'How do I prep for a GP appointment?',
  'What questions should I ask after a diagnosis?',
];

/* ─── Course Hero — Always-Visible Entry ─── */
const CourseHero = ({ onSubmit, generating, initialQuery, hasData }) => {
  const [query, setQuery] = useState(initialQuery || '');
  // Honest framing: "Personalised" only when we actually have something to
  // personalise on. Otherwise we show neutral example prompts and label them
  // as such — no false claims, no fake memory.
  const examples = hasData ? COURSE_EXAMPLES_PERSONAL : COURSE_EXAMPLES_NEUTRAL;
  const eyebrow = hasData ? 'Personalised to your data' : 'Try an example';
  const sub = hasData
    ? 'Understand your conditions, medications, and symptoms — all working together'
    : 'Examples of what Kindra can help with — nothing about you yet.';

  useEffect(() => {
    if (initialQuery) setQuery(initialQuery);
  }, [initialQuery]);

  const handleSubmit = () => {
    if (!query.trim() || generating) return;
    onSubmit(query.trim());
    setQuery('');
  };

  return (
    <div className="bg-[#235347] rounded-2xl p-5 pb-6" data-testid="course-hero" data-mode={hasData ? 'personalised' : 'examples'}>
      <p className="text-white/60 text-[10px] font-medium uppercase tracking-wider mb-1" data-testid="course-hero-eyebrow">{eyebrow}</p>
      <h2 className="text-base font-semibold text-white leading-snug mb-1" data-testid="course-hero-title">
        Build a course for your health
      </h2>
      <p className="text-[12px] text-white/70 leading-relaxed mb-4">
        {sub}
      </p>

      <div className="flex gap-2 mb-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          placeholder="What do you want to understand?"
          className="flex-1 bg-white/95 border-0 rounded-xl px-3.5 py-3 text-sm text-[#0F172A] outline-none placeholder:text-slate-400 focus:ring-2 focus:ring-white/30 transition-all"
          data-testid="course-request-input"
        />
        <button
          onClick={handleSubmit}
          disabled={!query.trim() || generating}
          className="bg-white text-[#235347] rounded-xl px-4 py-3 text-sm font-semibold hover:bg-white/90 disabled:opacity-50 transition-colors flex items-center gap-1.5 min-h-[44px] flex-shrink-0"
          data-testid="course-request-submit"
        >
          {generating ? <Spinner size={14} className="animate-spin" /> : 'Create course'}
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {examples.map((ex, i) => (
          <button
            key={i}
            onClick={() => setQuery(ex)}
            className="text-[10px] text-white/80 bg-white/12 px-2.5 py-1 rounded-full hover:bg-white/20 transition-colors"
            data-testid={`course-example-${i}`}
          >
            {ex}
          </button>
        ))}
      </div>
    </div>
  );
};

/* ─── Saved Course Card ─── */
const CourseCard = ({ course, onClick, onDelete }) => {
  const totalSteps = (course.sections || []).length;
  const currentStep = course.current_step || 0;
  const hasProgress = currentStep > 0 && currentStep < totalSteps;

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white border border-[#235347]/15 rounded-xl p-4 hover:shadow-sm transition-all group"
      data-testid={`course-card-${course.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-[#235347]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Notebook size={16} className="text-[#235347]" weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-[9px] font-bold uppercase tracking-wider text-[#235347]">Course</span>
          <p className="text-sm font-medium text-[#0F172A] leading-snug mt-0.5">{course.title}</p>
          <div className="mt-1.5">
            {hasProgress ? (
              <div className="flex items-center gap-1.5" data-testid={`course-progress-badge-${course.id}`}>
                <span className="text-[11px] font-medium text-[#235347]">Continue from Step {currentStep + 1}</span>
                <span className="text-[10px] text-slate-300">of {totalSteps}</span>
              </div>
            ) : (
              <p className="text-[11px] text-slate-400">
                {new Date(course.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                {' · '}{totalSteps} sections
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <span
            onClick={(e) => { e.stopPropagation(); onDelete(course.id); }}
            className="p-1.5 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`delete-course-${course.id}`}
          >
            <Trash size={13} className="text-slate-300 hover:text-red-400" />
          </span>
          {hasProgress ? (
            <CaretRight size={14} className="text-[#235347]" />
          ) : (
            <CaretRight size={14} className="text-slate-300 group-hover:text-slate-500 transition-colors" />
          )}
        </div>
      </div>
    </button>
  );
};

/* ─── Course Detail View (Guided Step-by-Step) ─── */
const SECTION_ACTION = {
  'What this is': null,
  'Why this matters for you': 'ask',
  'What to watch for': 'plan',
  'What to do day-to-day': 'bullets',
  'What can go wrong': 'ask',
  'How this connects to your other conditions': 'ask',
  'Questions for your doctor': 'plan',
};

/** Extract 1-2 line "why this matters" from the section */
const extractWhyNow = (sections) => {
  const sec = sections.find(s => s.heading === 'Why this matters for you');
  if (!sec?.content || typeof sec.content !== 'string') return '';
  const first = sec.content.match(/^[^.!?]+[.!?]/);
  if (first && first[0].length <= 140) return first[0];
  return sec.content.slice(0, 130).replace(/\s+\S*$/, '') + '...';
};

/** Extract "What you'll get" bullets from actions + watch + connections */
const extractOutcomes = (sections) => {
  const outcomes = [];
  const actionSec = sections.find(s => s.heading === 'What to do day-to-day');
  if (actionSec) outcomes.push('Know what to do day-to-day for your situation');
  const watchSec = sections.find(s => s.heading === 'What to watch for');
  if (watchSec) outcomes.push('Recognise the signs that matter for you');
  const qSec = sections.find(s => s.heading === 'Questions for your doctor');
  if (qSec) outcomes.push('Be clearer on what to ask your care team');
  return outcomes.slice(0, 3);
};

/** Extract "pay attention to" bullets from watch + risk sections */
const extractAttentionPoints = (sections) => {
  const points = [];
  for (const heading of ['What to watch for', 'What can go wrong']) {
    const sec = sections.find(s => s.heading === heading);
    if (!sec?.content || typeof sec.content !== 'string') continue;
    const sentences = sec.content.split(/(?<=[.!?])\s+/).filter(s => s.length > 20 && s.length < 110);
    if (sentences.length > 0 && points.length < 2) {
      points.push(sentences[0].replace(/^\s*[-•]\s*/, ''));
    }
  }
  return points;
};

/** Format text: max 2 sentences per paragraph, break into small chunks */
const formatSectionText = (content) => {
  if (typeof content !== 'string') return [content];
  // Split by double newlines first
  const rawParagraphs = content.split(/\n\n+/).map(p => p.trim()).filter(Boolean);
  const result = [];
  for (const para of rawParagraphs) {
    // Split long paragraphs into 2-sentence chunks
    const sentences = para.split(/(?<=[.!?])\s+/);
    for (let i = 0; i < sentences.length; i += 2) {
      const chunk = sentences.slice(i, i + 2).join(' ').trim();
      if (chunk) result.push(chunk);
    }
  }
  return result.length > 0 ? result : [content];
};

/** Derive micro-progress context line */
const getProgressContext = (course, currentHeading) => {
  const title = course.title || course.topic || 'your health';
  const short = title.length > 40 ? title.slice(0, 40).replace(/\s+\S*$/, '') + '...' : title;
  const map = {
    'What this is': `Understanding: ${short}`,
    'Why this matters for you': `Why this matters for your situation`,
    'What to watch for': `Learning what to watch for`,
    'What to do day-to-day': `Building your daily approach`,
    'What can go wrong': `Knowing what to be aware of`,
    'How this connects to your other conditions': `Connecting to your full health picture`,
    'Questions for your doctor': `Preparing for your next conversation`,
  };
  return map[currentHeading] || `Learning about your health`;
};

/** Extract outcome bullets for the completion screen */
const extractCompletionOutcomes = (sections, courseTitle) => {
  const outcomes = [];
  const whySec = sections.find(s => s.heading === 'Why this matters for you');
  if (whySec?.content) {
    const first = typeof whySec.content === 'string' ? whySec.content.match(/^[^.!?]+[.!?]/) : null;
    if (first) outcomes.push(`You understand: ${first[0].slice(0, 80)}`);
  }
  const actionSec = sections.find(s => s.heading === 'What to do day-to-day');
  if (actionSec) outcomes.push('You have a clear day-to-day plan you can follow');
  const watchSec = sections.find(s => s.heading === 'What to watch for');
  if (watchSec) outcomes.push('You know what signs to pay attention to');
  const connSec = sections.find(s => s.heading === 'How this connects to your other conditions');
  if (connSec) outcomes.push('You see how this connects to your other health factors');
  return outcomes.slice(0, 4);
};

const CourseHeaderCard = ({ course }) => {
  const sections = course.sections || [];
  const whyNow = extractWhyNow(sections);
  const outcomes = extractOutcomes(sections);
  const attention = extractAttentionPoints(sections);
  const isFallback = course.quality === 'fallback';

  // Build "Why this course" from related entities
  const entities = course.related_entities || [];
  const whyParts = [];
  const conditions = entities.filter(e => e.type === 'condition').map(e => e.name);
  const meds = entities.filter(e => e.type === 'medication').map(e => e.name);
  const symptoms = entities.filter(e => e.type === 'symptom').map(e => e.name);
  if (conditions.length > 0) whyParts.push(...conditions.slice(0, 2));
  if (meds.length > 0) whyParts.push(...meds.slice(0, 1));
  if (symptoms.length > 0) whyParts.push(...symptoms.slice(0, 1));
  const whyCourse = whyParts.length > 0
    ? `Based on your ${whyParts.join(', ')}`
    : '';

  return (
    <div className="rounded-2xl overflow-hidden mb-6" data-testid="course-header-card">
      {/* Title block */}
      <div className="bg-[#235347] p-5 pb-4">
        <p className="text-[10px] text-white/50 font-medium uppercase tracking-wider mb-2">Your health playbook</p>
        <h2 className="text-base font-semibold text-white leading-snug" data-testid="course-header-title">{course.title}</h2>
        {whyCourse && (
          <p className="text-[10px] text-white/50 mt-2" data-testid="course-header-why-created">{whyCourse}</p>
        )}
        {isFallback && (
          <div className="flex items-center gap-1.5 mt-2" data-testid="course-refining-badge">
            <Spinner size={10} className="text-white/50 animate-spin" />
            <p className="text-[10px] text-white/50">Simplified version — refining...</p>
          </div>
        )}
      </div>

      {/* Why this matters */}
      {whyNow && (
        <div className="bg-[#235347] px-5 pb-4">
          <p className="text-[10px] text-white/40 font-semibold uppercase tracking-wider mb-1">Why this matters to you right now</p>
          <p className="text-[12px] text-white/80 leading-relaxed line-clamp-2" data-testid="course-header-why">{whyNow}</p>
        </div>
      )}

      {/* What you'll get + What to pay attention to */}
      <div className="bg-[#1A3F36] px-5 py-4 space-y-4">
        {outcomes.length > 0 && (
          <div data-testid="course-header-outcomes">
            <p className="text-[10px] text-white/40 font-semibold uppercase tracking-wider mb-2">What you'll get from this</p>
            <div className="space-y-1.5">
              {outcomes.map((o, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CheckCircle size={12} className="text-emerald-400 mt-0.5 flex-shrink-0" weight="fill" />
                  <p className="text-[11px] text-white/75 leading-relaxed">{o}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {attention.length > 0 && (
          <div data-testid="course-header-attention">
            <p className="text-[10px] text-white/40 font-semibold uppercase tracking-wider mb-2">What to pay attention to</p>
            <div className="space-y-1.5">
              {attention.map((a, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Eye size={12} className="text-amber-400 mt-0.5 flex-shrink-0" weight="duotone" />
                  <p className="text-[11px] text-white/70 leading-relaxed line-clamp-2">{a}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CourseDetail = ({ course, onBack, onAskKindra, onAddToPlan, onComplete, onStepChange }) => {
  const navigate = useNavigate();
  const sections = course.sections || [];
  const total = sections.length;
  const [step, setStep] = useState(course.current_step || 0);
  const [completed, setCompleted] = useState(() => {
    const initial = new Set();
    for (let i = 0; i < (course.current_step || 0); i++) initial.add(i);
    return initial;
  });
  const [showExitMenu, setShowExitMenu] = useState(false);
  const [showResumeBanner, setShowResumeBanner] = useState(() => (course.current_step || 0) > 0);
  const isFinished = step >= total;

  // ─── Resume banner: fade out after 2.5s ───
  useEffect(() => {
    if (showResumeBanner) {
      const timer = setTimeout(() => setShowResumeBanner(false), 2500);
      return () => clearTimeout(timer);
    }
  }, [showResumeBanner]);

  // ─── Exit with "progress saved" toast ───
  const exitWithToast = useCallback(() => {
    toast('Your progress is saved. You can pick this up anytime.', { duration: 2500 });
    onBack();
  }, [onBack]);

  // ─── Swipe-back gesture (left edge swipe to exit) ───
  const touchStartRef = useRef(null);
  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    if (touch.clientX < 30) {
      touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
    } else {
      touchStartRef.current = null;
    }
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (!touchStartRef.current) return;
    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = Math.abs(touch.clientY - touchStartRef.current.y);
    const dt = Date.now() - touchStartRef.current.time;
    if (dx > 80 && dy < 100 && dt < 400) {
      exitWithToast();
    }
    touchStartRef.current = null;
  }, [exitWithToast]);

  useEffect(() => {
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchEnd]);

  const markDone = (idx) => setCompleted(prev => new Set([...prev, idx]));

  const persistStep = (newStep) => {
    if (onStepChange) onStepChange(course.id, newStep);
  };

  const handleContinue = () => {
    markDone(step);
    const nextStep = step + 1;
    if (step === total - 1 && onComplete) {
      onComplete(course.id);
    }
    setStep(nextStep);
    persistStep(Math.min(nextStep, total));
  };

  const handleBack = () => {
    if (step > 0) {
      const prevStep = step - 1;
      setStep(prevStep);
      persistStep(prevStep);
    }
  };

  const handlePauseAndExit = () => {
    setShowExitMenu(false);
    exitWithToast();
  };

  const handleStepFeedback = async (feedback) => {
    // Optimistic local update
    const updatedCourse = { ...course, step_feedback: { ...(course.step_feedback || {}), [String(step)]: { feedback, at: new Date().toISOString() } } };
    if (onStepChange) {
      // Piggyback on the course object update
    }
    try {
      await axios.post(`${API}/university/courses/${course.id}/step-feedback`, { step, feedback });
      // Force re-render with updated feedback
      course.step_feedback = updatedCourse.step_feedback;
    } catch { /* silent */ }
  };

  // Parse action bullets from a section
  const parseBullets = (content) => {
    if (Array.isArray(content)) return content;
    if (typeof content === 'string') {
      try { const p = JSON.parse(content); if (Array.isArray(p)) return p; } catch {}
      return content.split(/\n|•|—/).map(s => s.trim()).filter(Boolean);
    }
    return [];
  };

  // ─── Derived: Context-aware step header + Why line ───
  const currentSection = sections[step] || null;
  const SectionIcon = SECTION_ICONS[currentSection?.heading] || BookOpen;
  const isPersonal = currentSection?.heading === 'Why this matters for you';
  const isActions = currentSection?.heading === 'What to do day-to-day';
  const isRisk = currentSection?.heading === 'What can go wrong';
  const actionType = SECTION_ACTION[currentSection?.heading] || null;

  // Context header — sourced from course entities + health context
  const stepContextHeader = (() => {
    const entities = course.related_entities || [];
    const conditions = entities.filter(e => e.type === 'condition').map(e => e.name);
    const meds = entities.filter(e => e.type === 'medication').map(e => e.name);
    const heading = currentSection?.heading || '';
    if (heading.includes('matters') && conditions.length > 0) {
      return `Based on your ${conditions[0]}${conditions.length > 1 ? ` and ${conditions.length - 1} other condition${conditions.length > 2 ? 's' : ''}` : ''}`;
    }
    if (heading.includes('watch') || heading.includes('wrong')) {
      return meds.length > 0 ? `Considering your current medications` : 'Based on your health profile';
    }
    if (heading.includes('day-to-day') || heading.includes('do')) {
      return 'Personalised to your situation';
    }
    if (heading.includes('connects')) {
      return conditions.length > 1 ? `Looking at how your conditions interact` : 'Connecting to your full health picture';
    }
    if (heading.includes('doctor')) {
      return 'Preparing you for your next conversation';
    }
    return getProgressContext(course, heading);
  })();

  // "Why it matters" short line — extracted from the section or course context
  const stepWhyLine = (() => {
    const heading = currentSection?.heading || '';
    const title = course.title || course.topic || '';
    if (heading === 'What this is') return `Understanding the basics helps you make better decisions about ${title.toLowerCase().slice(0, 50)}.`;
    if (heading.includes('matters')) return null; // The section itself IS the "why"
    if (heading.includes('watch')) return 'Knowing what to look for means catching things early.';
    if (heading.includes('day-to-day')) return 'Small, consistent actions make the biggest difference.';
    if (heading.includes('wrong')) return 'Awareness helps you respond calmly and act quickly if needed.';
    if (heading.includes('connects')) return 'Your conditions don\'t exist in isolation — understanding the connections helps you manage them together.';
    if (heading.includes('doctor')) return 'Better questions lead to better care.';
    return null;
  })();

  // ─── Completion Screen (Soft — "Foundation Built") ───
  if (isFinished) {
    const actionSection = sections.find(s => s.heading === 'What to do day-to-day');
    const bullets = actionSection ? parseBullets(actionSection.content).slice(0, 3) : [];
    const completionOutcomes = extractCompletionOutcomes(sections, course.title);

    return (
      <div className="min-h-screen bg-[#FAFAF8]" data-testid="course-detail">
        <div className="bg-white border-b border-slate-100 sticky top-0 z-10 safe-top">
          <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3 min-h-[48px]">
            <button onClick={exitWithToast} className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700 transition-colors min-h-[44px] px-2 -ml-2 rounded-lg hover:bg-slate-50" data-testid="course-back-btn">
              <ArrowLeft size={16} />
              <span className="text-xs font-medium">Exit</span>
            </button>
            <div className="flex-1 min-w-0 text-center">
              <p className="text-sm font-medium text-[#0F172A] truncate">{course.title}</p>
            </div>
            <div className="w-16" />
          </div>
        </div>

        <div className="max-w-lg mx-auto px-4 py-10 pb-32" data-testid="course-complete">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-[#235347]/10 flex items-center justify-center mx-auto mb-4">
              <Trophy size={28} className="text-[#235347]" weight="duotone" />
            </div>
            <h2 className="text-lg font-semibold text-[#0F172A] mb-2">You've built a strong foundation</h2>
            <p className="text-sm text-slate-500 leading-relaxed max-w-xs mx-auto">We'll keep refining this with you as your health picture evolves.</p>
          </div>

          {completionOutcomes.length > 0 && (
            <div className="bg-[#235347]/5 border border-[#235347]/12 rounded-xl p-4 mb-4" data-testid="course-outcome">
              <p className="text-[11px] font-bold text-[#235347] uppercase tracking-wide mb-3">What you've learned</p>
              <div className="space-y-2.5">
                {completionOutcomes.map((o, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <CheckCircle size={13} className="text-[#235347] mt-0.5 flex-shrink-0" weight="fill" />
                    <p className="text-xs text-slate-600 leading-relaxed">{o}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {bullets.length > 0 && (
            <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200 mb-4" data-testid="course-complete-actions">
              <p className="text-xs font-bold text-[#0F172A] uppercase tracking-wide mb-3">Key actions</p>
              <div className="space-y-2.5">
                {bullets.map((b, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                    <p className="text-[13px] text-slate-700 leading-relaxed flex-1 line-clamp-2">{b}</p>
                    <button onClick={() => onAddToPlan(b)} className="p-1.5 hover:bg-emerald-100 rounded-md flex-shrink-0 min-w-[28px] min-h-[28px] flex items-center justify-center" data-testid={`complete-add-bullet-${i}`}>
                      <PlusCircle size={14} className="text-emerald-500" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-3">
            <button onClick={() => { if (bullets.length > 0) onAddToPlan(bullets[0]); else onAddToPlan(`Reviewed: ${course.title}`); toast.success('Added to your plan'); }}
              className="w-full bg-[#235347] text-white rounded-xl px-4 py-3.5 text-sm font-medium hover:bg-[#1A3F36] transition-colors flex items-center justify-center gap-2 min-h-[48px]"
              data-testid="course-complete-add-plan">
              <PlusCircle size={16} /> Add key actions to my plan
            </button>

            {course.related_entities?.length > 0 && (() => {
              const entity = course.related_entities.find(e => e.type === 'condition' || e.type === 'medication');
              if (!entity) return null;
              const suggestion = `How does ${entity.name} affect my daily life?`;
              return (
                <button onClick={() => { onBack(); setTimeout(() => { const input = document.querySelector('[data-testid="course-request-input"]'); if (input) { input.value = suggestion; input.dispatchEvent(new Event('input', { bubbles: true })); } }, 300); }}
                  className="w-full bg-[#235347]/5 border border-[#235347]/12 text-[#235347] rounded-xl px-4 py-3.5 text-sm font-medium hover:bg-[#235347]/10 transition-colors flex items-center justify-center gap-2 min-h-[48px]"
                  data-testid="course-complete-next-suggestion">
                  <Notebook size={16} /> Start next: {entity.name}
                </button>
              );
            })()}

            <button onClick={() => setStep(0)}
              className="w-full bg-white border border-slate-200 text-slate-600 rounded-xl px-4 py-3.5 text-sm font-medium hover:bg-slate-50 transition-colors min-h-[48px]"
              data-testid="course-complete-revisit">
              Revisit this course
            </button>
            <button onClick={exitWithToast}
              className="w-full text-sm text-slate-400 hover:text-slate-600 py-2 transition-colors"
              data-testid="course-complete-back">
              Back to learning
            </button>
          </div>

          {course.disclaimer && (
            <p className="text-[10px] text-slate-400 leading-relaxed mt-6 text-center">{course.disclaimer}</p>
          )}
        </div>
      </div>
    );
  }

  // ─── Step View ───

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="course-detail">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-10 safe-top">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3 min-h-[48px]">
          <button onClick={exitWithToast} className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700 transition-colors min-h-[44px] px-2 -ml-2 rounded-lg hover:bg-slate-50" data-testid="course-back-btn">
            <ArrowLeft size={16} />
            <span className="text-xs font-medium">Exit</span>
          </button>
          <div className="flex-1 min-w-0 text-center">
            <p className="text-sm font-medium text-[#0F172A] truncate">{course.title}</p>
          </div>
          <div className="w-16" />
        </div>

        {/* Progress bar */}
        <div className="max-w-lg mx-auto px-4 pb-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold text-slate-400" data-testid="course-step-label">Step {step + 1} of {total}</span>
            <span className="text-[11px] text-slate-300">{Math.round(((step) / total) * 100)}%</span>
          </div>
          <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#235347] rounded-full transition-all duration-300"
              style={{ width: `${((step + 1) / total) * 100}%` }}
              data-testid="course-progress-bar"
            />
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 pb-36">
        {/* Resume banner — shown briefly when re-entering a course with progress */}
        <AnimatePresence>
          {showResumeBanner && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.3 }}
              className="flex items-center gap-2 bg-[#235347]/5 border border-[#235347]/10 rounded-lg px-3.5 py-2.5 mb-5"
              data-testid="course-resume-banner"
            >
              <CheckCircle size={14} className="text-[#235347] flex-shrink-0" weight="fill" />
              <p className="text-xs text-[#235347]">Resuming from Step {step + 1}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Course header card — shown on step 0 */}
        {step === 0 && <CourseHeaderCard course={course} />}

        {/* Completed section summaries */}
        {step > 0 && (
          <div className="space-y-1.5 mb-6" data-testid="course-completed-steps">
            {sections.slice(0, step).map((sec, i) => {
              const fb = (course.step_feedback || {})[String(i)];
              return (
                <button
                  key={i}
                  onClick={() => setStep(i)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg bg-white border border-slate-100 hover:bg-slate-50 transition-colors text-left"
                  data-testid={`course-completed-step-${i}`}
                >
                  <CheckCircle size={14} className="text-[#235347] flex-shrink-0" weight="fill" />
                  <span className="text-[11px] text-slate-500 truncate flex-1">{sec.heading}</span>
                  {fb && (
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                      fb.feedback === 'yes' ? 'bg-emerald-50 text-emerald-600' :
                      fb.feedback === 'no' ? 'bg-red-50 text-red-500' :
                      'bg-slate-100 text-slate-400'
                    }`}>{fb.feedback === 'yes' ? 'Helpful' : fb.feedback === 'no' ? 'Not helpful' : 'Noted'}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Current section — structured as Insight / Why / Action / Feedback */}
        {currentSection && (
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            {/* Context-aware step header */}
            <div className="mb-4 px-1" data-testid="course-step-context-header">
              <p className="text-xs text-slate-400 leading-relaxed">
                {stepContextHeader}
              </p>
            </div>

            {/* A. INSIGHT — What the system sees */}
            <div
              className={`rounded-2xl p-5 border mb-4 ${
                isPersonal ? 'bg-[#235347]/5 border-[#235347]/15' :
                isRisk ? 'bg-amber-50/50 border-amber-200/60' :
                isActions ? 'bg-emerald-50 border-emerald-200' :
                'bg-white border-slate-200'
              }`}
              data-testid={`course-section-${step}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <SectionIcon size={15} className={
                  isPersonal ? 'text-[#235347]' :
                  isRisk ? 'text-amber-500' :
                  isActions ? 'text-emerald-600' :
                  'text-slate-500'
                } weight="duotone" />
                <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wide">{currentSection.heading}</h3>
              </div>
              {currentSection.relevance && (
                <span className={`inline-block text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded mb-3 ${
                  currentSection.priority === 'high' ? 'bg-red-50 text-red-500' :
                  currentSection.priority === 'medium' ? 'bg-amber-50 text-amber-600' :
                  'bg-slate-50 text-slate-400'
                }`} data-testid="step-priority-badge">{currentSection.priority || 'relevant'}</span>
              )}

              {isActions ? (
                <div className="space-y-3.5 mt-3">
                  {parseBullets(currentSection.content).slice(0, 5).map((bullet, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                      <p className="text-[13px] text-slate-700 leading-relaxed flex-1">{bullet}</p>
                      <button onClick={() => onAddToPlan(bullet)}
                        className="p-1.5 hover:bg-emerald-100 rounded-md flex-shrink-0 min-w-[28px] min-h-[28px] flex items-center justify-center"
                        data-testid={`step-add-bullet-${i}`}>
                        <PlusCircle size={14} className="text-emerald-500" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3 mt-3">
                  {formatSectionText(currentSection.content).map((para, i) => (
                    <p key={i} className="text-[13px] text-slate-600 leading-relaxed">{para}</p>
                  ))}
                </div>
              )}

              {isPersonal && <p className="text-[11px] text-slate-400 mt-4">Based on your data</p>}
            </div>

            {/* B. WHY IT MATTERS — extracted short explanation */}
            {stepWhyLine && (
              <div className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 mb-4" data-testid="course-step-why">
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Why this matters</p>
                <p className="text-xs text-slate-600 leading-relaxed">{stepWhyLine}</p>
              </div>
            )}

            {/* C. ACTION — clear next step */}
            {actionType === 'ask' && (
              <button onClick={onAskKindra}
                className="w-full flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-3 mb-4 hover:bg-slate-50 transition-colors text-left"
                data-testid="course-step-ask">
                <ChatCircle size={14} className="text-[#235347]" />
                <span className="text-xs text-slate-600 font-medium">Ask Kindra about this</span>
              </button>
            )}
            {actionType === 'plan' && (
              <button onClick={() => onAddToPlan(`${currentSection.heading}: ${course.title}`)}
                className="w-full flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-3 mb-4 hover:bg-slate-50 transition-colors text-left"
                data-testid="course-step-plan">
                <PlusCircle size={14} className="text-[#235347]" />
                <span className="text-xs text-slate-600 font-medium">Add a reminder to my plan</span>
              </button>
            )}

            {/* D. FEEDBACK — simple response */}
            <div className="bg-white border border-slate-100 rounded-xl px-4 py-3" data-testid="course-step-feedback">
              <p className="text-[11px] text-slate-400 mb-2.5">Was this helpful?</p>
              <div className="flex items-center gap-2">
                {[
                  { value: 'yes', label: 'Yes', activeClass: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
                  { value: 'no', label: 'No', activeClass: 'bg-red-50 border-red-200 text-red-600' },
                  { value: 'not_sure', label: 'Not sure', activeClass: 'bg-slate-100 border-slate-200 text-slate-600' },
                ].map(({ value, label, activeClass }) => {
                  const currentFb = (course.step_feedback || {})[String(step)]?.feedback;
                  const isActive = currentFb === value;
                  return (
                    <button
                      key={value}
                      onClick={() => handleStepFeedback(value)}
                      className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-colors min-h-[36px] ${
                        isActive ? activeClass : 'border-slate-200 text-slate-400 hover:bg-slate-50'
                      }`}
                      data-testid={`step-feedback-${value}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Bottom nav: Back / Continue + Exit options */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-slate-100 z-20"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            {step > 0 ? (
              <button
                onClick={handleBack}
                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors min-h-[44px] px-3"
                data-testid="course-step-back"
              >
                <ArrowLeft size={14} />
                Back
              </button>
            ) : (
              <div />
            )}
            <button
              onClick={handleContinue}
              className="flex-1 bg-[#235347] text-white rounded-xl px-4 py-3 text-sm font-medium hover:bg-[#1A3F36] transition-colors flex items-center justify-center gap-2 min-h-[48px]"
              data-testid="course-step-continue"
            >
              {step < total - 1 ? (
                <>Continue <ArrowRight size={14} /></>
              ) : (
                <>Finish course <Check size={14} /></>
              )}
            </button>
          </div>
          {/* Soft exit options */}
          <div className="flex items-center justify-center gap-4 mt-2.5 pb-0.5">
            <button
              onClick={handlePauseAndExit}
              className="text-xs text-slate-400 hover:text-slate-600 transition-colors py-1"
              data-testid="course-pause-exit"
            >
              Pause course
            </button>
            <span className="text-slate-200">·</span>
            <button
              onClick={() => { exitWithToast(); setTimeout(() => navigate('/home'), 50); }}
              className="text-xs text-slate-400 hover:text-slate-600 transition-colors py-1"
              data-testid="course-return-dashboard"
            >
              Return to dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Topic Card ─── */
const TopicCard = ({ topic, onClick }) => {
  const config = TYPE_CONFIG[topic.type] || TYPE_CONFIG.condition;
  const Icon = config.icon;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left bg-white border ${config.border} rounded-xl p-4 hover:shadow-sm transition-all group`}
      data-testid={`topic-card-${topic.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
          <Icon size={16} className={config.color} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className={`text-[9px] font-bold uppercase tracking-wider ${config.color}`}>{config.label}</span>
            {topic.relevance === 'high' && (
              <span className="text-[8px] bg-amber-100 text-amber-700 font-semibold px-1.5 py-0.5 rounded-full">Important</span>
            )}
          </div>
          <p className="text-sm font-medium text-[#0F172A] leading-snug">{topic.title}</p>
          <p className="text-[11px] text-slate-500 mt-1 line-clamp-2 leading-relaxed">{topic.summary}</p>
        </div>
        <CaretRight size={14} className="text-slate-300 group-hover:text-slate-500 transition-colors flex-shrink-0 mt-1" />
      </div>
    </button>
  );
};

/* ─── Action Bullets Section ─── */
const ActionBullets = ({ content, topicId, topicTitle, onAddToPlan }) => {
  let bullets = [];
  
  // Handle different content formats
  if (Array.isArray(content)) {
    bullets = content;
  } else if (typeof content === 'string') {
    try {
      const parsed = JSON.parse(content);
      if (Array.isArray(parsed)) bullets = parsed;
    } catch {
      bullets = content.split(/\n|•|—/).map(s => s.trim()).filter(Boolean);
    }
  }
  
  if (bullets.length === 0) return null;

  // Cap at 5 bullets for readability
  const displayBullets = bullets.slice(0, 5);

  return (
    <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200" data-testid="topic-section-actions">
      <div className="flex items-center gap-2 mb-3">
        <Target size={14} className="text-emerald-600" weight="fill" />
        <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wide">What to do right now</h3>
      </div>
      <div className="space-y-3">
        {displayBullets.map((bullet, i) => (
          <div key={i} className="flex items-start gap-2.5 group">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
            <p className="text-[13px] text-slate-700 leading-relaxed flex-1 line-clamp-3">{bullet}</p>
            <button
              onClick={() => onAddToPlan(bullet)}
              className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-emerald-100 rounded-md transition-all flex-shrink-0 min-w-[28px] min-h-[28px] flex items-center justify-center"
              title="Add to my plan"
              data-testid={`add-bullet-${i}`}
            >
              <PlusCircle size={14} className="text-emerald-500" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Topic Detail View ─── */
const TopicDetail = ({ topic, content, loading, onBack, onAskKindra, onFeedback, onAddToPlan }) => {
  const config = TYPE_CONFIG[topic.type] || TYPE_CONFIG.condition;
  const navigate = useNavigate();

  const renderSection = (section, i) => {
    const isWhyNow = section.heading === 'Why this matters right now';
    const isActionBullets = section.heading === 'What to do right now';
    const SectionIcon = SECTION_ICONS[section.heading] || BookOpen;

    if (isWhyNow) {
      return (
        <div key={i} className={`${config.bg} rounded-xl p-4 border ${config.border}`} data-testid="topic-section-why-now">
          <div className="flex items-center gap-2 mb-2.5">
            <Lightning size={14} className={config.color} weight="fill" />
            <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wide">{section.heading}</h3>
          </div>
          <div className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-line line-clamp-[8]">{section.content}</div>
          <p className="text-[10px] text-slate-400 mt-2">Based on your data</p>
        </div>
      );
    }

    if (isActionBullets) {
      return <ActionBullets key={i} content={section.content} topicId={topic.id} topicTitle={topic.title} onAddToPlan={onAddToPlan} />;
    }

    return (
      <div key={i} className="bg-white rounded-xl p-4 border border-slate-100" data-testid={`topic-section-${i}`}>
        <div className="flex items-center gap-2 mb-2.5">
          <SectionIcon size={14} className={config.color} weight="duotone" />
          <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wide">{section.heading}</h3>
        </div>
        <div className="text-[13px] text-slate-600 leading-relaxed whitespace-pre-line line-clamp-[12]">{section.content}</div>
      </div>
    );
  };

  const relatedEntities = content?.related_entities || [];

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="topic-detail">
      <div className="bg-white border-b border-slate-100 sticky top-0 z-10 safe-top">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3 min-h-[48px]">
          <button onClick={onBack} className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-slate-100 rounded-lg" data-testid="topic-back-btn">
            <ArrowLeft size={18} className="text-slate-600" />
          </button>
          <div className="flex-1 min-w-0">
            <p className={`text-[9px] font-bold uppercase tracking-wider ${config.color}`}>{config.label}</p>
            <p className="text-sm font-medium text-[#0F172A] truncate">{topic.title}</p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-5 pb-32">
        {loading ? (
          <div className="flex flex-col items-center py-16 gap-3">
            <Spinner size={28} className="text-slate-400 animate-spin" />
            <p className="text-sm text-slate-400">Generating personalised content...</p>
          </div>
        ) : content ? (
          <>
            {/* Ensure "Why this matters" and "What to do" render first */}
            {(() => {
              const sections = content.sections || [];
              const whyNow = sections.filter(s => s.heading === 'Why this matters right now');
              const actions = sections.filter(s => s.heading === 'What to do right now');
              const rest = sections.filter(s => s.heading !== 'Why this matters right now' && s.heading !== 'What to do right now');
              return [...whyNow, ...actions, ...rest].map((section, i) => renderSection(section, i));
            })()}

            {/* This connects to */}
            {relatedEntities.length > 0 && (
              <div className="bg-white rounded-xl p-4 border border-slate-100" data-testid="related-entities">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2.5">This connects to</p>
                <div className="flex flex-wrap gap-2">
                  {relatedEntities.map((e, i) => {
                    const tc = TYPE_CONFIG[e.type] || TYPE_CONFIG.condition;
                    const TypeIcon = tc.icon;
                    return (
                      <button
                        key={i}
                        onClick={() => navigate(tc.path)}
                        className={`inline-flex items-center gap-1.5 text-[11px] ${tc.bg} ${tc.border} border rounded-full px-3 py-1.5 font-medium text-slate-600 hover:shadow-sm transition-all min-h-[32px]`}
                        data-testid={`related-entity-${i}`}
                      >
                        <TypeIcon size={12} className={tc.color} weight="duotone" />
                        {e.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Feedback: 3 actions */}
            <div className="bg-white rounded-xl p-4 border border-slate-100" data-testid="topic-feedback">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => onFeedback(topic.id, true)}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-medium transition-all min-h-[40px] ${
                    topic.user_feedback === 'relevant'
                      ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                      : 'bg-slate-50 text-slate-500 border border-slate-200 hover:bg-emerald-50 active:bg-emerald-100'
                  }`}
                  data-testid="feedback-relevant"
                >
                  {topic.user_feedback === 'relevant' ? <CheckCircle size={14} weight="fill" /> : <ThumbsUp size={14} />}
                  This helps me
                </button>
                <button
                  onClick={() => onFeedback(topic.id, false)}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-medium transition-all min-h-[40px] ${
                    topic.user_feedback === 'not_relevant'
                      ? 'bg-slate-200 text-slate-600 border border-slate-300'
                      : 'bg-slate-50 text-slate-500 border border-slate-200 hover:bg-slate-100 active:bg-slate-200'
                  }`}
                  data-testid="feedback-not-relevant"
                >
                  <ThumbsDown size={14} />
                  Not relevant
                </button>
                <button
                  onClick={() => {
                    const actions = content?.sections?.find(s => s.heading === 'What to do right now');
                    if (actions) {
                      let bullets = [];
                      if (Array.isArray(actions.content)) {
                        bullets = actions.content;
                      } else if (typeof actions.content === 'string') {
                        try { bullets = JSON.parse(actions.content); } catch { bullets = [actions.content]; }
                      }
                      if (bullets.length > 0) { onAddToPlan(bullets[0]); }
                    } else {
                      onAddToPlan(`Review: ${topic.title}`);
                    }
                  }}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-medium bg-[#235347]/5 text-[#235347] border border-[#235347]/20 hover:bg-[#235347]/10 active:bg-[#235347]/15 transition-all min-h-[40px]"
                  data-testid="add-to-plan-btn"
                >
                  <PlusCircle size={14} />
                  Add to my plan
                </button>
              </div>
            </div>

            {content.disclaimer && (
              <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                <p className="text-[10px] text-slate-400 leading-relaxed">{content.disclaimer}</p>
              </div>
            )}
          </>
        ) : (
          <p className="text-center text-sm text-slate-400 py-12">Unable to load content.</p>
        )}
      </div>

      {!loading && content && (
        <div className="fixed bottom-0 left-0 right-0 flex justify-center z-20 px-4 pb-4"
          style={{ paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))' }}
        >
          <button onClick={onAskKindra}
            className="bg-[#0F172A] text-white rounded-full px-5 py-3 text-sm font-medium shadow-lg hover:bg-slate-800 transition-colors flex items-center gap-2 min-h-[48px]"
            data-testid="ask-kindra-btn"
          >
            <ChatCircle size={16} weight="fill" />
            Ask Kindra about this
          </button>
        </div>
      )}
    </div>
  );
};

/* ─── Main University Page ─── */
const LearnPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [topics, setTopics] = useState([]);
  const [weeklyLesson, setWeeklyLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [topicContent, setTopicContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Course state
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseGenerating, setCourseGenerating] = useState(false);
  const [courseLimitReached, setCourseLimitReached] = useState(false);

  // Sprint B — toggle for the longer "Browse all" list (hidden by default)
  const [showAllLessons, setShowAllLessons] = useState(false);

  // Pre-fill from navigation state (e.g. from Health page)
  const prefillTopic = location.state?.prefillCourse || '';
  const resumeCourseId = location.state?.resumeCourseId || '';

  // Auto-resume course from Daily Brain
  useEffect(() => {
    if (resumeCourseId && courses.length > 0 && !selectedCourse) {
      const found = courses.find(c => c.id === resumeCourseId);
      if (found) setSelectedCourse(found);
    }
  }, [resumeCourseId, courses, selectedCourse]);

  const fetchTopics = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/university/topics`);
      setTopics(data.topics || []);
      setWeeklyLesson(data.weekly_lesson || null);
    } catch {
      toast.error('Failed to load topics');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCourses = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/university/courses`);
      setCourses(data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchTopics();
    fetchCourses();
  }, [fetchTopics, fetchCourses]);

  const handleTopicClick = async (topic) => {
    setSelectedTopic(topic);
    setTopicContent(null);

    if (topic.content_generated && topic.content) {
      setTopicContent(topic.content);
      return;
    }

    setContentLoading(true);
    try {
      const { data } = await axios.get(`${API}/university/topics/${topic.id}`);
      setTopicContent(data.content);
      setTopics(prev => prev.map(t =>
        t.id === topic.id ? { ...t, content: data.content, content_generated: true } : t
      ));
    } catch {
      toast.error('Failed to load topic');
    } finally {
      setContentLoading(false);
    }
  };

  const handleRequestCourse = async (topicText) => {
    setCourseGenerating(true);
    setCourseLimitReached(false);
    console.log('[Kindra] Course creation requested:', topicText?.slice(0, 50));

    try {
      const { data } = await axios.post(`${API}/university/courses`, { topic: topicText });
      console.log('[Kindra] Course created:', { id: data?.id, quality: data?.quality, title: data?.title });
      setCourses(prev => [data, ...prev]);
      setSelectedCourse(data);

      if (data.quality === 'fallback') {
        toast('Simplified course created — refining it now...', { duration: 5000 });
        triggerRefine(data.id);
      } else {
        toast.success('Course ready', { description: data.title });
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const errorCode = typeof detail === 'object' ? detail?.code : null;
      const errorMsg = typeof detail === 'object' ? detail?.message : (typeof detail === 'string' ? detail : null);

      console.error('[Kindra] Course creation failed:', { status, errorCode, errorMsg });

      if (status === 409 && errorCode === 'COURSE_LIMIT_REACHED') {
        // User hit active course limit — show in-page guidance, NOT a toast
        setCourseLimitReached(true);
        // Scroll to the active courses section
        setTimeout(() => {
          document.querySelector('[data-testid="your-learning"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      } else if (status === 500 && errorCode === 'COURSE_GENERATION_FAILED') {
        // AI + fallback both failed — offer retry
        toast.error(errorMsg || 'Course generation failed', {
          action: { label: 'Try again', onClick: () => handleRequestCourse(topicText) },
          duration: 8000,
        });
      } else {
        // Any other error
        toast.error(errorMsg || 'Something went wrong — please try again', {
          action: { label: 'Retry', onClick: () => handleRequestCourse(topicText) },
          duration: 8000,
        });
      }
    } finally {
      setCourseGenerating(false);
    }
  };

  const triggerRefine = async (courseId) => {
    try {
      const { data } = await axios.post(`${API}/university/courses/${courseId}/refine`);
      if (data.status === 'refined' && data.course) {
        setCourses(prev => prev.map(c => c.id === courseId ? data.course : c));
        setSelectedCourse(prev => prev?.id === courseId ? data.course : prev);
        toast.success('Course updated with full details');
      }
    } catch { /* silent — fallback course is still usable */ }
  };

  const handleDeleteCourse = async (courseId) => {
    setCourses(prev => prev.filter(c => c.id !== courseId));
    setCourseLimitReached(false); // Slot freed — clear limit banner
    try {
      await axios.delete(`${API}/university/courses/${courseId}`);
    } catch {
      fetchCourses();
    }
  };

  const handleCourseAskKindra = () => {
    if (!selectedCourse) return;
    navigate('/chat', { state: { prefillMessage: `I was taking a course on "${selectedCourse.title}" in Kindra University. Can you help me understand this better?` } });
  };

  const handleCourseAddToPlan = async (text) => {
    if (!selectedCourse || !text) return;
    try {
      await axios.post(`${API}/university/plan`, {
        text,
        topic_id: selectedCourse.id,
        topic_title: selectedCourse.title,
      });
      toast.success('Added to your plan', { description: text.slice(0, 60) + (text.length > 60 ? '...' : '') });
    } catch {
      toast.error('Failed to add to plan');
    }
  };

  const handleCompleteCourse = async (courseId) => {
    try {
      await axios.patch(`${API}/university/courses/${courseId}/complete`);
      setCourses(prev => prev.map(c =>
        c.id === courseId ? { ...c, status: 'completed', completed_at: new Date().toISOString() } : c
      ));
      setCourseLimitReached(false); // Slot freed — clear limit banner
    } catch { /* ignore */ }
  };

  const handleStepChange = async (courseId, step) => {
    try {
      await axios.patch(`${API}/university/courses/${courseId}/progress`, { current_step: step });
      setCourses(prev => prev.map(c =>
        c.id === courseId ? { ...c, current_step: step } : c
      ));
    } catch { /* ignore */ }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.post(`${API}/university/topics/refresh`);
      setTopics(data.topics || []);
      toast.success('Topics refreshed');
    } catch {
      toast.error('Failed to refresh');
    } finally {
      setRefreshing(false);
    }
  };

  const handleAskKindra = () => {
    if (!selectedTopic) return;
    const context = selectedTopic.source_entity
      ? `I was reading about "${selectedTopic.title}" in Kindra University. This relates to my ${selectedTopic.type} "${selectedTopic.source_entity}". Can you help me understand this better?`
      : `I was reading about "${selectedTopic.title}" in Kindra University. Can you help me understand this better?`;
    navigate('/chat', { state: { prefillMessage: context } });
  };

  const handleFeedback = async (topicId, relevant) => {
    setTopics(prev => prev.map(t =>
      t.id === topicId ? { ...t, user_feedback: relevant ? 'relevant' : 'not_relevant' } : t
    ));
    if (selectedTopic?.id === topicId) {
      setSelectedTopic(prev => ({ ...prev, user_feedback: relevant ? 'relevant' : 'not_relevant' }));
    }
    try {
      await axios.post(`${API}/university/topics/${topicId}/feedback`, { relevant });
    } catch {
      toast.error('Failed to save feedback');
    }
  };

  const handleAddToPlan = async (text) => {
    if (!selectedTopic || !text) return;
    try {
      await axios.post(`${API}/university/plan`, {
        text,
        topic_id: selectedTopic.id,
        topic_title: selectedTopic.title,
      });
      toast.success('Added to your plan', { description: text.slice(0, 60) + (text.length > 60 ? '...' : '') });
    } catch {
      toast.error('Failed to add to plan');
    }
  };

  // ─── Course Detail View ───
  if (selectedCourse) {
    return (
      <CourseDetail
        course={selectedCourse}
        onBack={() => setSelectedCourse(null)}
        onAskKindra={handleCourseAskKindra}
        onAddToPlan={handleCourseAddToPlan}
        onComplete={handleCompleteCourse}
        onStepChange={handleStepChange}
      />
    );
  }

  // ─── Topic Detail View ───
  if (selectedTopic) {
    return (
      <TopicDetail
        topic={selectedTopic}
        content={topicContent}
        loading={contentLoading}
        onBack={() => { setSelectedTopic(null); setTopicContent(null); }}
        onAskKindra={handleAskKindra}
        onFeedback={handleFeedback}
        onAddToPlan={handleAddToPlan}
      />
    );
  }

  const groupOrder = ['condition', 'medication', 'symptom', 'appointment', 'specialist'];

  // Split courses into active and completed

  const activeCourses = courses.filter(c => c.status !== 'completed').slice(0, 3);
  const completedCourses = courses.filter(c => c.status === 'completed');

  // ─── Top 3 personalised lessons (Sprint B) ───────────────────────────────
  // Pick the highest-relevance topics across ALL types. Add a "Why this matters
  // for you" sentence grounded in the topic's source data (condition/med/symptom).
  const topThreeLessons = [...topics]
    .sort((a, b) => {
      const relOrder = { high: 0, medium: 1, low: 2 };
      return (relOrder[a.relevance] ?? 1) - (relOrder[b.relevance] ?? 1);
    })
    .slice(0, 3);

  const whyForLesson = (t) => {
    // Backend already provides a personalised why_now string per topic — prefer it.
    if (t?.why_now && typeof t.why_now === 'string' && t.why_now.length > 8) {
      return t.why_now;
    }
    const subject = t.source_entity || t.subject_name || t.related_to || t.condition_name || t.medication_name || '';
    if (t.type === 'condition' && subject) return `Because you have ${subject} in your record — understanding it helps you ask better questions at appointments.`;
    if (t.type === 'medication' && subject) return `Because you take ${subject} — knowing how it actually works helps you spot when something's off.`;
    if (t.type === 'symptom' && subject) return `Because you've flagged ${subject} — patterns and triggers become much easier to notice once you see how it usually works.`;
    if (t.type === 'interaction') return `Because two things in your record can interact — Kindra wants you to know what to watch for.`;
    if (t.type === 'appointment' && subject) return `Because your ${subject} is coming up — preparing this in advance leads to a much better conversation.`;
    return `Because Kindra noticed this is genuinely useful for you right now.`;
  };

  return (
    <MainLayout>
      <div className="max-w-3xl lg:max-w-5xl mx-auto space-y-5 pb-4" data-testid="university-page">
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.18em]">Understand</p>
            <h1
              className="font-serif text-[26px] sm:text-[30px] leading-tight tracking-[-0.01em] text-[#0E1C2E] font-normal mt-0.5"
              data-testid="learn-title"
            >
              What do I need to <em className="italic">understand</em> today?
            </h1>
            <p className="text-[12.5px] text-slate-500 mt-1">Plain-English answers, drawn from your record when relevant.</p>
          </div>
          <button onClick={handleRefresh} disabled={refreshing}
            className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
            data-testid="refresh-topics-btn"
            aria-label="Refresh"
          >
            <ArrowsClockwise size={18} className={`text-slate-400 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* ═══ Sprint C — This week's best lesson (hero) ═══ */}
        {weeklyLesson && (
          <ThisWeeksBestLesson
            lesson={weeklyLesson}
            onStart={(lesson) => {
              const target = topics.find((t) => t.id === lesson.topic_id) || lesson;
              handleTopicClick(target);
            }}
          />
        )}

        {/* ═══ Wave 2 P2 — Based on your health (PRIMARY) ═══
            Was "Recommended for you" further down. Promoted above the Course
            builder so the user always sees something contextual to their data
            on first paint. The list rendering below is unchanged — only the
            section eyebrow was reframed. */}
        {!loading && topThreeLessons.length > 0 && (
          <section data-testid="based-on-your-health">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.18em] mb-3 px-1">
              Based on your health · {topThreeLessons.length}
            </p>
            {completedCourses.length === 0 && activeCourses.length === 0 && (
              <p
                className="text-[12.5px] italic text-slate-500 leading-relaxed px-1 mb-3"
                data-testid="learn-foundation-prompt"
              >
                Start one of these lessons to begin building your foundation.
              </p>
            )}
            <div className="space-y-3">
              {topThreeLessons.map((t, idx) => (
                <button
                  key={`top-${t.id}-${idx}`}
                  onClick={() => handleTopicClick(t)}
                  className="w-full text-left bg-white rounded-2xl border border-slate-200 hover:border-slate-300 transition-all p-5 group"
                  data-testid={`recommended-lesson-${idx}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#235347]/8 border border-[#235347]/12 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="font-serif italic text-[16px] text-[#235347]">{idx + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[15px] font-medium text-[#0E1C2E] leading-snug">
                        {t.title}
                      </p>
                      <p className="text-[13px] text-slate-600 mt-1.5 leading-relaxed line-clamp-2">
                        {t.description || t.summary || ''}
                      </p>
                      <p className="text-[11.5px] italic text-slate-500 mt-2.5 pt-2.5 border-t border-slate-100 leading-relaxed">
                        <span className="not-italic font-semibold text-slate-400 uppercase tracking-[0.14em] text-[9.5px] mr-1.5">Why for you</span>
                        {whyForLesson(t)}
                      </p>
                      <span className="mt-3 inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#0E1C2E]">
                        Start lesson
                        <CaretRight size={12} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* ═══ Wave 2 P2 — graceful fallback for users with zero topics ═══
            Empty state previously read "No topics yet · Add conditions…",
            which left the page abstract. Now the page falls through to the
            course builder + browse-all section instead — no orphan empty card. */}
        {!loading && topThreeLessons.length === 0 && (
          <p className="text-[12.5px] italic text-slate-500 leading-relaxed px-1" data-testid="learn-no-record-yet">
            Add a condition, medication, or report to your record and Kindra will surface lessons here.
          </p>
        )}

        {/* ═══ Sprint C — Your progress ═══ */}
        <ProgressCard
          active={courses.filter((c) => c.status !== 'completed').length}
          completed={courses.filter((c) => c.status === 'completed').length}
          streak={0}
        />

        {/* ═══ Course Hero — DEMOTED to secondary entry below the lessons ═══ */}
        <CourseHero onSubmit={handleRequestCourse} generating={courseGenerating} initialQuery={prefillTopic} hasData={topics.length > 0} />

        {/* ═══ Course Generation Loading ═══ */}
        {courseGenerating && (
          <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-4" data-testid="course-generating">
            <Spinner size={16} className="text-[#235347] animate-spin flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-[#0F172A]">Generating your course...</p>
              <p className="text-[11px] text-slate-400">This takes a few seconds</p>
            </div>
          </div>
        )}

        {/* ═══ Course Limit Reached Banner ═══ */}
        {courseLimitReached && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="course-limit-banner">
            <div className="flex items-start gap-3">
              <Warning size={18} className="text-amber-500 flex-shrink-0 mt-0.5" weight="duotone" />
              <div className="flex-1">
                <p className="text-sm font-medium text-[#0F172A] mb-1">You have 3 active courses</p>
                <p className="text-[12px] text-slate-500 leading-relaxed mb-3">
                  Complete or remove one of your active courses below to start a new one.
                </p>
                <button
                  onClick={() => {
                    setCourseLimitReached(false);
                    document.querySelector('[data-testid="your-learning"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }}
                  className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-800 rounded-lg px-3 py-2 text-xs font-medium hover:bg-amber-200 transition-colors"
                  data-testid="course-limit-view-courses"
                >
                  <Eye size={13} weight="bold" /> View active courses
                </button>
              </div>
              <button
                onClick={() => setCourseLimitReached(false)}
                className="p-1 text-amber-400 hover:text-amber-600 transition-colors"
                data-testid="course-limit-dismiss"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        )}

        {/* ═══ Your Learning (active courses, max 3) ═══ */}
        {activeCourses.length > 0 ? (
          <section data-testid="your-learning">
            <div className="flex items-center gap-1.5 mb-2 px-1">
              <Notebook size={12} className="text-[#235347]" weight="duotone" />
              <span className="text-[9px] font-bold uppercase tracking-wider text-[#235347]">Your learning</span>
              <span className="text-[9px] text-slate-300">({activeCourses.length})</span>
            </div>
            <div className="space-y-2">
              {activeCourses.map(course => (
                <CourseCard
                  key={course.id}
                  course={course}
                  onClick={() => setSelectedCourse(course)}
                  onDelete={handleDeleteCourse}
                />
              ))}
            </div>
          </section>
        ) : !courseGenerating && (
          <div className="text-center py-3" data-testid="no-active-courses">
            <p className="text-xs text-slate-400">You haven't started a course yet — try creating one above</p>
          </div>
        )}

        {/* ═══ Foundation Built (formerly "Completed") ═══ */}
        {completedCourses.length > 0 && (
          <section data-testid="completed-learning">
            <div className="flex items-center gap-1.5 mb-2 px-1">
              <CheckCircle size={12} className="text-slate-400" weight="duotone" />
              <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Foundation built</span>
              <span className="text-[9px] text-slate-300">({completedCourses.length})</span>
            </div>
            <div className="space-y-2">
              {completedCourses.map(course => (
                <button
                  key={course.id}
                  onClick={() => setSelectedCourse(course)}
                  className="w-full text-left bg-white/70 border border-slate-100 rounded-xl p-3.5 hover:bg-white transition-all flex items-center gap-3"
                  data-testid={`completed-course-${course.id}`}
                >
                  <CheckCircle size={16} className="text-emerald-400 flex-shrink-0" weight="fill" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-500 truncate">{course.title}</p>
                    <p className="text-[10px] text-slate-400">
                      Foundation built {course.completed_at ? new Date(course.completed_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : ''} · Revisit anytime
                    </p>
                  </div>
                  <CaretRight size={14} className="text-slate-300 flex-shrink-0" />
                </button>
              ))}
            </div>
          </section>
        )}

        {/* ═══ Auto-generated Topics — "Browse all" disclosure only ═══
            Wave 2 P2: the primary "Based on your health" section above now
            renders the top 3 personalised lessons. The block below stays as a
            fallback for users with >3 topics so they can browse all. The empty
            and loading states are also retained — gracefully handled. */}
        {loading ? (
          <div className="flex flex-col items-center py-16 gap-3">
            <GraduationCap size={32} className="text-slate-300" weight="duotone" />
            <Spinner size={20} className="text-slate-400 animate-spin" />
            <p className="text-sm text-slate-400">Generating your lessons...</p>
          </div>
        ) : topics.length > 3 && (
          <div className="pt-2" data-testid="browse-all-section">
            <button
              onClick={() => setShowAllLessons(!showAllLessons)}
              className="w-full flex items-center justify-between bg-white border border-slate-200 rounded-xl px-4 py-3 hover:border-slate-300 transition-colors"
              data-testid="browse-all-toggle"
            >
              <span className="text-[13px] font-medium text-slate-600">
                {showAllLessons ? 'Hide other lessons' : `Browse all ${topics.length} lessons`}
              </span>
              <CaretRight
                size={14}
                className={`text-slate-400 transition-transform ${showAllLessons ? 'rotate-90' : ''}`}
              />
            </button>

            {showAllLessons && (
              <>
                <div className="flex items-center gap-1.5 px-1 pt-5 mb-3">
                  <GraduationCap size={12} className="text-slate-400" weight="duotone" />
                  <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">All lessons</span>
                </div>
                <div className="space-y-5">
                  {groupOrder.map(type => {
                    const group = topics
                      .filter(t => t.type === type)
                      .sort((a, b) => {
                        const relOrder = { high: 0, medium: 1, low: 2 };
                        return (relOrder[a.relevance] ?? 1) - (relOrder[b.relevance] ?? 1);
                      });
                    if (group.length === 0) return null;
                    const config = TYPE_CONFIG[type] || TYPE_CONFIG.condition;
                    return (
                      <div key={type}>
                        <div className="flex items-center gap-1.5 mb-2 px-1">
                          <span className={`text-[9px] font-bold uppercase tracking-wider ${config.color}`}>{config.label}s</span>
                          <span className="text-[9px] text-slate-300">({group.length})</span>
                        </div>
                        <div className="space-y-2">
                          {group.map((topic, idx) => (
                            <TopicCard key={`${topic.id}-${idx}`} topic={topic} onClick={() => handleTopicClick(topic)} />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default LearnPage;
